import { useT } from "@transifex/react";
import type { SchoolClass, StudentTestContent, StudentTestContentWrapper } from "p6m-p6u";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { ShareModes } from "../../enums/shareFlow";
import { formatDateWithTime } from "../../utils/dateUtils";
import { ClassActivityCard } from "./ClassActivityCard";
import type { ClassActivity, TestActivity, VocabTrainingActivity } from "./ClassActivityCardsOverview.types";
import { VocabTrainingCardExpandedContent } from "./VocabTrainingCardExpandedContent";
import { TestCardExpandedContent } from "./TestCardExpandedContent";
import type { useTestOperations } from "../../hooks/useTestOperations";
import type { OpenShareFlowParams, useVocabTrainingOperations } from "../../hooks/useVocabTrainingOperations";
import ActivityVisibilityBadge from "../../basic/activityVisibilityBadge/ActivityVisibilityBadge";
import { getIsLegacyTest, resolveShareModeWithLegacy } from "../../helpers/LegacyTest";
import {
    ActivityVisibility,
    resolveTestActivityVisibility,
    resolveVocabActivityVisibility,
    type ActivityVisibilityResult,
} from "../../helpers/ActivityVisibility";
import { useNow } from "../../hooks/useNow";

export interface ClassActivityListTranslations {
    vocabularyFrom: (source: string) => string;
    oclock: string;
    lastModifiedOn: string;
    createdOn: string;
    noFilterResults: string;
    vocabTrainingsLoadError: string;
    userTestsLoadError: string;
    activitiesLoadError: string;
}

interface ClassActivityListProps {
    activities: ClassActivity[];
    schoolClasses: SchoolClass[];
    studentTestContentWrapper: StudentTestContentWrapper;
    classFilter: string;
    languageFilter: string;
    testOps: ReturnType<typeof useTestOperations>;
    vocabTrainingOps: ReturnType<typeof useVocabTrainingOperations>;
    onShareVocabTraining: (params: OpenShareFlowParams) => void;
    vocabTrainingsLoadError: boolean;
    userTestsLoadError: boolean;
    translations: ClassActivityListTranslations;
}

export const ClassActivityList = (props: ClassActivityListProps) => {
    const t = useT();
    // Subscribed for periodic re-render so PLANNED rows flip to VISIBLE when their date passes.
    useNow();

    // Custom key: the keyless "Test" string translates to "Aufgabe" (phase6's feature name), but
    // share-mode labels must read "Test" in German too. The key must be an inline literal (the
    // Transifex CLI extractor can't resolve constants) and identical at every share-mode label.
    const t_testHeader = t("Test", { _key: "test.mode.label", _tags: "ClassActivity" });
    const t_practiceHeader = t("Practice", { _tags: "ClassActivity" });

    const getTestHeaderLabel = (activityId: string): string | undefined => {
        const sharedTestContent = props.studentTestContentWrapper[activityId];
        if (!sharedTestContent) return undefined;
        return resolveShareModeWithLegacy(sharedTestContent) === ShareModes.PRACTICE ? t_practiceHeader : t_testHeader;
    };

    const getClassLabel = (activity: ClassActivity): string | undefined => {
        const activitySchoolClassId =
            activity.category === ClassActivityCategory.TEST
                ? activity.testData.groupId
                : activity.vocabTrainingData.groupId;
        if (!activitySchoolClassId) return undefined;
        return props.schoolClasses.find((c) => c.token === activitySchoolClassId)?.name;
    };

    const getLoadErrorMessage = () => {
        if (props.userTestsLoadError && props.vocabTrainingsLoadError) {
            return props.translations.activitiesLoadError;
        }
        if (props.userTestsLoadError) {
            return props.translations.userTestsLoadError;
        }
        if (props.vocabTrainingsLoadError) {
            return props.translations.vocabTrainingsLoadError;
        }
        return null;
    };

    const getTestVisibilityForActivity = (activity: TestActivity): ActivityVisibilityResult | null => {
        if (!activity.testData.groupId) return null;
        const isShared = props.testOps.isTestShared(activity.id);
        const sharedTestContent: StudentTestContent | null = props.studentTestContentWrapper[activity.id] ?? null;
        // Wrapper not yet hydrated for a shared test — suppress badge to avoid HIDDEN flicker.
        if (isShared && !sharedTestContent) return null;
        // Legacy tests pre-date the Enhanced Classes share-mode split and don't carry visibility state.
        if (getIsLegacyTest(sharedTestContent)) return null;
        return resolveTestActivityVisibility(sharedTestContent?.visibilityDate);
    };

    const renderEditableBadge = (visibilityResult: ActivityVisibilityResult | null, onEdit: () => void) =>
        visibilityResult ? (
            <ActivityVisibilityBadge
                visibility={visibilityResult.visibility}
                isEditable
                plannedDate={visibilityResult.plannedDate}
                onEdit={onEdit}
            />
        ) : undefined;

    const renderTestContent = (activity: TestActivity, visibilityResult: ActivityVisibilityResult | null) => {
        const isShared = props.testOps.isTestShared(activity.id);
        const sharedTestContent: StudentTestContent | null = props.studentTestContentWrapper[activity.id] ?? null;
        // The share record decides; testData is a defensive fallback. Both resolve legacy shares to TEST.
        const shareMode = isShared ? resolveShareModeWithLegacy(sharedTestContent ?? activity.testData) : null;
        const isSharedTest = shareMode === ShareModes.TEST;
        const onEditVisibility = isShared
            ? () => props.testOps.handleOpenEditActivityVisibility(activity.id)
            : () => props.testOps.handleShareTest(activity.id, activity.testData.groupId);
        return (
            <TestCardExpandedContent
                testId={activity.id}
                dateLabel={
                    activity.testData.updatedDate
                        ? `${props.translations.lastModifiedOn} ${formatDateWithTime(
                              activity.testData.updatedDate,
                              props.translations.oclock
                          )}`
                        : ""
                }
                isShared={isShared}
                isSharedTest={isSharedTest}
                sharedTestContent={sharedTestContent}
                selectedSchoolClassId={activity.testData.groupId}
                availableSchoolClasses={props.schoolClasses}
                onPreview={() => props.testOps.handleOpenTestPreview(activity.id)}
                onShare={() => props.testOps.handleShareTest(activity.id, activity.testData.groupId)}
                onCopyLink={() => props.testOps.handleCopyTestLink(activity.id)}
                onGrade={() => props.testOps.handleNavigateToReview(activity.id)}
                onEditDetails={() => props.testOps.handleOpenEditTestDetails(activity.id)}
                onViewSubmissions={() => props.testOps.handleNavigateToReview(activity.id)}
                onConfigurePdf={() => props.testOps.handleOpenPdfConfiguration(activity.id)}
                onDownloadPdf={() => props.testOps.setDownloadPopupTestDetails(activity.testData)}
                visibilityBadge={renderEditableBadge(visibilityResult, onEditVisibility)}
            />
        );
    };

    const getVocabVisibilityForActivity = (activity: VocabTrainingActivity): ActivityVisibilityResult | null => {
        // Vocab trainings have no legacy state; the share-mode split was test-only.
        if (!activity.vocabTrainingData.groupId) return null;
        const metadata = props.vocabTrainingOps.vocabMetadata[activity.id];
        // Cache miss (metadata still loading) — hide the badge rather than decode HIDDEN. Safe here
        // because the badge is passive; the share button can't do this and live-resolves via GET
        // (handleShareButtonClick) so it never re-shares an already-shared training.
        if (!metadata) return null;
        return resolveVocabActivityVisibility(metadata.visibilityDate);
    };

    const renderVocabTrainingContent = (
        activity: VocabTrainingActivity,
        visibilityResult: ActivityVisibilityResult | null
    ) => {
        const triggerShare = () =>
            props.onShareVocabTraining({
                training: activity.vocabTrainingData,
                bookName: activity.contentSourceName,
                vocabularyCount: activity.vocabCount,
            });
        // Vocab trainings have no shared-copy record, so HIDDEN (null visibilityDate) is the
        // not-yet-shared state — route it into the share flow, not the edit-visibility modal.
        const onEditVisibility =
            visibilityResult && visibilityResult.visibility !== ActivityVisibility.HIDDEN
                ? () => props.vocabTrainingOps.handleOpenEditActivityVisibility(activity.vocabTrainingData)
                : triggerShare;
        return (
            <VocabTrainingCardExpandedContent
                vocabTraining={activity.vocabTrainingData}
                bookInfo={activity.bookInfo}
                ownSubjectInfo={activity.ownSubjectInfo}
                vocabCount={activity.vocabCount}
                dateLabel={
                    activity.vocabTrainingData.createdDate
                        ? `${props.translations.createdOn} ${formatDateWithTime(
                              new Date(activity.vocabTrainingData.createdDate).getTime(),
                              props.translations.oclock
                          )}`
                        : ""
                }
                selectedSchoolClassId={activity.vocabTrainingData.groupId}
                availableSchoolClasses={props.schoolClasses}
                onShare={triggerShare}
                visibilityBadge={renderEditableBadge(visibilityResult, onEditVisibility)}
            />
        );
    };

    const renderExpandedContent = (activity: ClassActivity, visibilityResult: ActivityVisibilityResult | null) => {
        switch (activity.category) {
            case ClassActivityCategory.TEST:
                return renderTestContent(activity, visibilityResult);
            case ClassActivityCategory.VOCAB_TRAINING:
                return renderVocabTrainingContent(activity, visibilityResult);
            default:
                return null;
        }
    };

    const getVisibilityForActivity = (activity: ClassActivity): ActivityVisibilityResult | null => {
        switch (activity.category) {
            case ClassActivityCategory.TEST:
                return getTestVisibilityForActivity(activity);
            case ClassActivityCategory.VOCAB_TRAINING:
                return getVocabVisibilityForActivity(activity);
            default:
                return null;
        }
    };

    const loadErrorMessage = getLoadErrorMessage();

    return (
        <div
            className="flex flex-col gap-s w-full"
            style={{ maxWidth: `${process.env.REACT_APP_UPPER_GRID_STEP_BORDER}px` }}
        >
            {loadErrorMessage && (
                <div className="flex items-center justify-center py-m">
                    <p className="text-center text-p6-grey-dark">{loadErrorMessage}</p>
                </div>
            )}
            {props.activities.map((activity) => {
                const visibilityResult = getVisibilityForActivity(activity);
                return (
                    <ClassActivityCard
                        key={activity.id}
                        id={activity.id}
                        subtitle={
                            // A saved title (chip/custom) stands alone; only the legacy book/subject
                            // fallback still needs the "Vocabulary from …" framing.
                            activity.category === ClassActivityCategory.VOCAB_TRAINING &&
                            !activity.vocabTrainingData.testName
                                ? props.translations.vocabularyFrom(activity.subtitle)
                                : activity.subtitle
                        }
                        bookCover={activity.bookCover}
                        ownSubjectInfo={activity.ownSubjectInfo}
                        classActivityCategory={activity.category}
                        headerLabel={
                            activity.category === ClassActivityCategory.TEST
                                ? getTestHeaderLabel(activity.id)
                                : undefined
                        }
                        classLabel={getClassLabel(activity)}
                        onPrimaryAction={activity.onPrimaryAction}
                        onEdit={activity.onEdit}
                        onDuplicate={activity.onDuplicate}
                        onDelete={activity.onDelete}
                        isEditDisabled={
                            activity.category === ClassActivityCategory.TEST && props.testOps.isTestShared(activity.id)
                        }
                        subtitleBadge={
                            visibilityResult ? (
                                <ActivityVisibilityBadge
                                    visibility={visibilityResult.visibility}
                                    isEditable={false}
                                    plannedDate={visibilityResult.plannedDate}
                                />
                            ) : undefined
                        }
                    >
                        {renderExpandedContent(activity, visibilityResult)}
                    </ClassActivityCard>
                );
            })}
            {!loadErrorMessage && props.activities.length === 0 && (props.languageFilter || props.classFilter) && (
                <div className="min-h-[33vh] flex items-center justify-center">
                    <p className="text-center text-xl">{props.translations.noFilterResults}</p>
                </div>
            )}
        </div>
    );
};
